/* eslint-disable camelcase */

import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import { LocalizedMessage } from 'intl/LocalizedComponents';
// HOCS
import withFormManagement from 'hocs/WithFormManagement';
import withLoading from 'hocs/WithLoading';
import withTitle from 'hocs/WithTitle';
// Core components
import Box from 'components/core/Box';
// Models
import mOnDemand from 'models/cpm/onDemand';
import mFinance from 'models/cpm/finance';
import { mutLoginByMagicLink, getTokenIsOnDemand, getReferenceFromToken } from 'models/auth';
// Utils
import UrlUtils from 'utils/UrlUtils';
// Custom
import {
  cpmSearchOnDemandAccess,
  cpmGetOnDemandAccess,
  cpmSelfCheckoutOnDemandAccess,
  postLoginByMagicLink,
  reinitState,
  reinitAuth,
} from 'actions';
// HOC
import withStateReinit from 'hocs/WithStateReinit';

import { cpmOnDemandAccessForCheckoutDataShape, cpmSlugCustomizationShape } from 'constants/PropTypesShapes';
import ActionLoader from 'components/loaders/ActionLoader';
import { navigateToPurchase } from 'purchase/navigation';
import CpmClientTemplate from 'components/templates/apps/CpmClientTemplate';
import CheckoutHeader from 'cpmCheckout/assets/CheckoutHeader';
import Callout from 'components/misc/Callout';
import { BackContainedButton } from 'components/buttons/ContainedButtons';
import { FlowLogoImg } from 'components/img/AssetImg';
import CheckoutLicensePlate from './CheckoutLicensePlate';
import CheckoutExitChoice from './CheckoutExitChoice';
import CheckoutTemporaryExitEnd from './CheckoutTemporaryExitEnd';
import CheckoutInfo from './CheckoutInfo';
import CheckoutTutorial from './CheckoutTutorial';

const Checkout = ({
  slugId,
  location: { query, pathname },
  customization,

  authToken,
  dispatchLoginByMagicLink,

  cpmOnDemand,

  cpmOnDemandSearch,
  dispatchSearchOnDemandAccess,

  cpmOnDemandGetError,
  dispatchGetOnDemandAccess,
  cpmOnDemandSearchError,

  cpmOnDemandCheckout,
  dispatchCheckout,

  onErrorsChange,
  onResetForm,
  errors,

  dispatchReinitAuth,
  dispatchReinitState,
  handleStartLoading,
}) => {
  const [showTemporaryExitForm, setShowTemporaryExitForm] = React.useState(false);

  React.useEffect(function getOnDemandFromAuthTokenInStore() {
    const thereIsOnDemandTokenInStore = authToken && getTokenIsOnDemand(authToken) && !cpmOnDemand;
    const comingFromSupportTeam = query && query.origin === 'support';

    if (thereIsOnDemandTokenInStore && !comingFromSupportTeam) {
      const onDemandReference = getReferenceFromToken(authToken);
      dispatchGetOnDemandAccess({ reference: onDemandReference });
    }
  }, []);

  /* Logo and title */
  /* -------------- */

  const FlowLogo = <div style={{ marginBottom: 40 }}>{FlowLogoImg(220)}</div>;
  const showLogo = customization && !customization.hideHeaderLogo;

  const LogoAndTitle = showLogo
    ? <CheckoutHeader customization={customization} isFirstPage={!cpmOnDemand} />
    : FlowLogo;

  /* Errors */
  /* ------ */
  const removeAllErrors = () => {
    onResetForm();
    UrlUtils.removeQueryParams(query, pathname, ['error']);
  };

  const GlobalError = errors ? (
    <Callout severity="error" withInfoIcon>
      <LocalizedMessage id={`error.${errors.global}`} />
    </Callout>
  ) : null;

  const expiredOffer = query && query.error === 'paymentCancelled';
  const ExpiredOfferError = expiredOffer ? (
    <Callout severity="error" centerContent className="mb-2">
      <LocalizedMessage id="info.checkoutExpiredOffer" />
    </Callout>
  ) : null;

  const ErrorMessages = (
    <div className="mb-3 w-100">
      {GlobalError}
      {ExpiredOfferError}
    </div>
  );

  /* License plate */
  /* ------------- */
  const handleSearchOnDemandAcces = ({ licensePlate }) => {
    removeAllErrors();
    dispatchSearchOnDemandAccess({ licensePlate });
  };
  const onDemandNotCheckedOut = cpmOnDemand && !cpmOnDemand.selfCheckoutType;
  const shouldShowTutorial = customization && customization.showOnDemandCheckoutTutorial;

  const LicensePlate = (
    <CheckoutLicensePlate
      defaultLicensePlate={_get(query, 'licensePlate')}
      globalErrors={errors}
      onValidate={handleSearchOnDemandAcces}
    />
  );
  const Tutorial = shouldShowTutorial ? <CheckoutTutorial /> : null;

  const LicensePlateAndTutorial = !onDemandNotCheckedOut ? (
    <React.Fragment>
      {LicensePlate}
      {Tutorial}
    </React.Fragment>
  ) : null;
  /* ✅ ⬇️ */
  React.useEffect(function getAuthTokenFromSearchByLicensePlate() {
    if (cpmOnDemandSearch) {
      const { carpark: { slug: onDemandSlug } } = cpmOnDemandSearch;

      // If the on-demand received doesn't have a paymentStatus set to 'required' we display a message that encourages user to read again instructions in mail instead of going to the checkout page
      const paymentIsNotRequired = cpmOnDemandSearch.paymentStatus && !mFinance.lastPurchasePaymentIsRequired(cpmOnDemandSearch);
      if (paymentIsNotRequired) onErrorsChange({ global: 'searchOnDemandAccess.paymentNotRequired' });

      // If slug from current session and slug linked to the on-demand search resultat mismatch, we redirect to the right checkout page for this on-demand
      if (onDemandSlug.id !== slugId) {
        const { domain, slug } = onDemandSlug;
        const { licensePlate } = cpmOnDemandSearch;
        handleStartLoading();
        UrlUtils.redirectToCpmClientLocation(domain, `${slug}/`, `/checkout?licensePlate=${licensePlate}`);
      }

      // We use the magic link provided by the on-demand search result to log in and store an auth token (👉 state.auth.token)
      dispatchLoginByMagicLink({ link: cpmOnDemandSearch.magicLink });
    }
  }, [cpmOnDemandSearch]);
  // /* ❌ ⬇️ */
  React.useEffect(function removeAuthTokenIfError() {
    if (cpmOnDemandGetError || cpmOnDemandSearchError) dispatchReinitAuth();
  }, [cpmOnDemandGetError, cpmOnDemandSearchError]);

  /* Choice between definitive or temporary exit */
  /* ------------------------------------------ */

  const checkoutDefinitiveExit = () => dispatchCheckout(mOnDemand.serializeSelfCheckout({ type: mOnDemand.getSelfCheckoutTypeDefinitiveExit.value, reference: cpmOnDemand.reference }));
  const checkoutTemporaryExit = ({ estimatedEnd }) => dispatchCheckout(mOnDemand.serializeSelfCheckout({ estimatedEnd, type: mOnDemand.getSelfCheckoutTypeTemporaryExit.value, reference: cpmOnDemand.reference }));
  const cleanAllStore = () => {
    dispatchReinitState('cpmOnDemand');
    dispatchReinitAuth();
  };

  const DefinitiveOrTemporaryExit = (onDemandNotCheckedOut && !showTemporaryExitForm) ? (
    <React.Fragment>
      <div className="mb-3 w-100">
        <CheckoutInfo onDemand={cpmOnDemand} withPriceGrid />
      </div>
      <CheckoutExitChoice
        onDefinitiveExit={checkoutDefinitiveExit}
        onTemporaryExit={() => setShowTemporaryExitForm(true)}
      />
      <div className="mt-5">
        <BackContainedButton
          color="secondary"
          onClick={cleanAllStore}
        />
      </div>
    </React.Fragment>
  ) : null;
  /* ✅ ⬇️ */
  React.useEffect(function redirectToPurchasePageAfterSelfCheckout() {
    if (cpmOnDemandCheckout) {
      const purchase = mFinance.lastPurchase(cpmOnDemandCheckout);
      navigateToPurchase({
        onDemandReference: cpmOnDemandCheckout.reference,
        id: purchase.id,
      });
    }
  }, [cpmOnDemandCheckout]);

  /* New exit end */
  /* ------------ */

  const TemporaryNewExitEnd = (onDemandNotCheckedOut && showTemporaryExitForm) ? (
    <React.Fragment>
      <div className="mb-3 w-100">
        <CheckoutInfo onDemand={cpmOnDemand} />
      </div>
      <CheckoutTemporaryExitEnd onDemand={cpmOnDemand} onValidate={checkoutTemporaryExit} />
      <div className="mt-5">
        <BackContainedButton
          color="secondary"
          onClick={() => setShowTemporaryExitForm(false)}
        />
      </div>
    </React.Fragment>
  ) : null;

  /* ========================================================= */
  /* UI                                                        */
  /* ========================================================= */
  const MAX_WIDTH = 430;

  return (
    <CpmClientTemplate hideLogo={!showLogo}>
      <Box type="defaultBackground" className="d-flex flex-column mx-auto align-items-center px-3" style={{ maxWidth: MAX_WIDTH }}>
        {LogoAndTitle}
        {ErrorMessages}
        {LicensePlateAndTutorial}
        {DefinitiveOrTemporaryExit}
        {TemporaryNewExitEnd}
      </Box>
      <ActionLoader />
    </CpmClientTemplate>
  );
};

Checkout.propTypes = {
  cpmOnDemand: cpmOnDemandAccessForCheckoutDataShape,
  cpmOnDemandSearch: cpmOnDemandAccessForCheckoutDataShape,
  cpmOnDemandGetError: PropTypes.object,
  cpmOnDemandSearchError: PropTypes.object,
  cpmOnDemandCheckout: cpmOnDemandAccessForCheckoutDataShape,
  authToken: PropTypes.string,
  customization: cpmSlugCustomizationShape,
  slugId: PropTypes.number.isRequired,
  errors: PropTypes.object,
  location: PropTypes.shape({
    query: PropTypes.object.isRequired,
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onResetForm: PropTypes.func.isRequired,
  dispatchGetOnDemandAccess: PropTypes.func.isRequired,
  dispatchSearchOnDemandAccess: PropTypes.func.isRequired,
  dispatchCheckout: PropTypes.func.isRequired,
  dispatchLoginByMagicLink: PropTypes.func.isRequired,
  dispatchReinitAuth: PropTypes.func.isRequired,
  dispatchReinitState: PropTypes.func.isRequired,
  handleStartLoading: PropTypes.func.isRequired,
};

Checkout.defaultProps = {
  authToken: null,
  cpmOnDemand: null,
  cpmOnDemandGetError: null,
  cpmOnDemandSearch: null,
  cpmOnDemandSearchError: null,
  cpmOnDemandCheckout: null,
  customization: null,
  errors: null,
};

const mapOwnPropsToResources = (ownProps) => ({
  operationError: {
    ...ownProps.cpmOnDemandSearchError,
    ...ownProps.cpmOnDemandGetError,
    ...ownProps.cpmOnDemandCheckoutError,
  },
});

const mapStateToProps = (state) => ({
  slugId: state.cpmSlug.current.result.id,
  customization: _get(state.cpmSlug.current.result, 'customization'),
  authToken: _get(state.auth, 'token'),

  cpmOnDemand: _get(state.cpmOnDemand, 'result'),

  cpmOnDemandSearch: _get(state.cpmOnDemand.search, 'result'),
  cpmOnDemandSearchError: _get(state.cpmOnDemand.search, 'error'),

  cpmOnDemandGet: _get(state.cpmOnDemand.get, 'result'),
  cpmOnDemandGetError: _get(state.cpmOnDemand.get, 'error'),

  cpmOnDemandCheckout: _get(state.cpmOnDemand.checkout, 'result'),
  cpmOnDemandCheckoutError: _get(state.cpmOnDemand.checkout, 'error'),
  cpmOnDemandCheckoutDone: _get(state.cpmOnDemand.checkout, 'done'),

});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps;

  return {
    ...ownProps,
    ...stateProps,
    dispatchReinitState: (target) => dispatch(reinitState(target)),
    dispatchReinitAuth: () => dispatch(reinitAuth()),
    dispatchLoginByMagicLink: (data) => dispatch(postLoginByMagicLink(mutLoginByMagicLink, { ...data, slugId: stateProps.slugId })),

    dispatchSearchOnDemandAccess: (data) => dispatch(cpmSearchOnDemandAccess(mOnDemand.queSearchOnDemandAccess, { ...data, slugId: stateProps.slugId })),
    dispatchGetOnDemandAccess: (data) => dispatch(cpmGetOnDemandAccess(mOnDemand.queGetOnDemandAccess, { ...data, slugId: stateProps.slugId })),

    dispatchCheckout: (data) => dispatch(cpmSelfCheckoutOnDemandAccess(mOnDemand.mutSelfCheckoutOnDemandAccess, { ...data, slugId: stateProps.slugId })),
  };
};

module.exports = compose(
  connect(mapStateToProps, null, mergeProps),
  withFormManagement(null, mapOwnPropsToResources, false),
  withTitle('pageTitle.checkout'),
  withStateReinit(['cpmOnDemand']),
  withLoading,
)(Checkout);



// WEBPACK FOOTER //
// ./routes/checkout/components/Checkout.js